import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { PlusCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { dateFromString, formatDateAsAnniversary, type BirthdayDate } from 'helpers/dateTimes';

import 'react-datepicker/dist/react-datepicker.css';

/**
 * Le libellé d'une date dans une chip de filtre. Partagé avec le filtre de plage : les deux chips
 * doivent lire pareil. Accepte un jour (`YYYY-MM-DD`), un `Date` ou un horodatage.
 */
export function formatFilterDate(value: BirthdayDate | Date | number): string {
    const date = typeof value === 'string' ? dateFromString(value) : new Date(value);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

type DataTableDateFilterProps = {
    title: string;
    /** Un jour au format `YYYY-MM-DD`. `''` = filtre absent. */
    value: BirthdayDate;
    onChange: (value: BirthdayDate) => void;
};

export function DataTableDateFilter({ title, value, onChange }: DataTableDateFilterProps) {
    const [isOpen, setIsOpen] = useState(false);
    const isActive = value !== '';

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', isActive && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {isActive && (
                        <>
                            <Separator orientation="vertical" className="mx-1 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal text-xs">
                                {formatFilterDate(value)}
                            </Badge>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                    <DatePicker
                        // `dateFromString` lit le jour en heure locale : `new Date('2026-09-10')` le
                        // lirait en UTC et reculerait d'un jour à l'ouest de Greenwich.
                        selected={isActive ? dateFromString(value) : null}
                        onChange={(date: Date | null) => {
                            onChange(date ? formatDateAsAnniversary({ dateTime: date }) : '');
                            setIsOpen(false);
                        }}
                        inline
                        locale={fr}
                        dateFormat="dd/MM/yyyy"
                    />
                </div>
                {isActive && (
                    <div className="border-t p-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs"
                            onClick={() => {
                                onChange('');
                                setIsOpen(false);
                            }}
                        >
                            Effacer
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
